/* eslint-disable react/forbid-prop-types */
/* eslint-disable no-unsafe-optional-chaining */
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Forward5Icon from "@mui/icons-material/Forward5";
import HistoryIcon from "@mui/icons-material/History";
import KeyIcon from "@mui/icons-material/Key";
import LockIcon from "@mui/icons-material/Lock";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MDBadgeDot from "components/MDBadgeDot";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { postAPICall } from "lib/callApi";
import { UPDATE_DAILY_GOALS } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { generateColorFromString } from "utils/GenerateColorFromString/GenerateColorFromString";
import { numberFormatter } from "utils/HelperFunctions";
import Loader from "utils/Loader";
import TooltipLight from "utils/TooltipLight";
import PopperWithHtml from "utils/PopperWithHtml";
import DailyGoalModal from "./DailyGoalModal";
import DailyPointsInformationModal from "./DailyPointsInformationModal";
import KpiTrackerOptions from "./KpiTrackerOptions";
import UserKpiInput from "./UserKpiInput";

function UserKpi({
  dailyInfoLoading,
  kpiEntriesLoading,
  kpiEntries,
  handleActionEdit,
  competitions,
  dailyInfo,
  handleSortByOrderOfKpi,
  refetchKpiEntries,
}) {
  const theme = useTheme();
  const [isDailyPointsInfoOpen, setIsDailyPointsInfoOpen] = useState(false);
  const [isDailyGoalModalOpen, setIsDailyGoalModalOpen] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [syncIntervalId, setSyncIntervalId] = useState(null);
  const [hasIconRow, setHasIconRow] = useState(false);
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));
  // Keeping for now, we will bring this back with more functionality
  // const [modalData, setModalData] = useState({
  //   open: false,
  //   cb: () => null,
  //   fields: [],
  // });
  // const [modalFieldsState, setModalFieldsState] = useState({});

  useEffect(() => {
    let sync;
    let iconRow;
    kpiEntries.forEach((entry) => {
      const competitionsWithKPIActivity = competitions?.filter((competition) =>
        competition?.actions?.find((action) => action?.kpiActionId === entry?.kpiActionId)
      );

      if (competitionsWithKPIActivity?.length || entry?.lastSynced || entry?.isSynced)
        iconRow = true;

      if (entry?.lastSynced) {
        if (!sync || sync > entry.lastSynced) {
          sync = entry.lastSynced;
        }
      }
    });

    if (sync) {
      setLastSynced(`Last synced: ${sync} minutes ago`);
    }
    if (iconRow) {
      setHasIconRow(true);
    }
  }, [kpiEntries, competitions]);

  useEffect(() => {
    if (lastSynced && !syncIntervalId) {
      setSyncIntervalId(
        setInterval(() => {
          setLastSynced((prev) => {
            const prevNum = prev?.match?.(/\d+/)?.[0];
            if (Number(prevNum)) {
              return `Last synced: ${Number(prevNum) + 1} minutes ago`;
            }
            return prev;
          });
        }, 60000)
      );
    }

    return () => {
      if (syncIntervalId) {
        clearInterval(syncIntervalId);
        setSyncIntervalId(null);
      }
    };
  }, [lastSynced, syncIntervalId]);

  const generateTrophies = (kpiAction) => {
    const competitionsWithKPIActivity = competitions?.filter((competition) =>
      competition?.actions?.find((action) => action?.kpiActionId === kpiAction?.kpiActionId)
    );

    if (!competitionsWithKPIActivity?.length) return null;

    return competitionsWithKPIActivity.map((competition) => (
      <img
        key={competition?.Badge?.name}
        src={competition?.Badge?.badgeUrl}
        alt={competition?.Badge?.name}
        height="24px"
        width="24px"
      />
    ));
  };

  const getItemStyle = (isDragging, draggableStyle) => {
    const computedStyles = {
      background: isDragging ? "rgba(0, 0, 0, 0.04)" : "inherit",
      ...draggableStyle,
    };

    if (isDragging) {
      computedStyles.display = "flex";
      computedStyles.justifyContent = "space-between";
      computedStyles.paddingLeft = "2rem";
      computedStyles.paddingRight = "14rem";
    }
    return computedStyles;
  };

  const onDragEnd = (result) => {
    if (
      !result.destination ||
      result.destination.index >
        kpiEntries.reduce((max, obj) => (obj.sortByOrder > max ? obj.sortByOrder : max), 0)
    )
      return;
    handleSortByOrderOfKpi(
      result.draggableId.split("-")[1],
      result.destination.index === 0 ? 1 : result.destination.index
    );
  };

  const handleSaveDailyGoals = async (newKpiData) => {
    const response = await postAPICall(UPDATE_DAILY_GOALS, true, {
      kpiData: newKpiData,
    });
    if (response?.data?.success) {
      refetchKpiEntries();
      enqueueSnackbar(
        JSON.stringify({
          icon: "success",
          title: "Success",
          text: `Updated Daily Goals!`,
        }),
        {
          variant: "custom",
        }
      );
    } else {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: response?.response?.data?.message || "Something went wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const renderKpiInput = (kpi) => <UserKpiInput kpi={kpi} handleActionEdit={handleActionEdit} />;

  return (
    <Grid
      container
      spacing={2}
      sx={{
        "& .MuiPaper-root": {},
      }}
    >
      <Grid item xs={12} sx={{ position: "relative" }}>
        <Loader loading={kpiEntriesLoading} />
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(droppableProvided) => (
              <TableContainer
                {...droppableProvided.droppableProps}
                ref={droppableProvided.innerRef}
                onMouseLeave={() => setHoveredRow(null)}
                component={Paper}
                sx={{
                  userSelect: "none",
                  height: "100%",
                }}
              >
                <Stack direction="row" justifyContent="space-between">
                  <MDBox
                    sx={{
                      pt: "1rem",
                      pl: "1.5rem",
                      display: "flex",
                    }}
                  >
                    <MDTypography variant="h6" sx={{ color: "info" }} mb={0}>
                      KPI Tracker
                    </MDTypography>
                    {dailyInfo?.length > 0 && (
                      <KeyIcon
                        onClick={() => setIsDailyPointsInfoOpen(true)}
                        sx={{
                          cursor: "pointer",
                          ml: "0.5rem",
                          "&.MuiSvgIcon-root": {
                            fontSize: "28px !important",
                            color: `${theme.palette.iconBlue.main} !important`,
                          },
                          pb: "2px",
                        }}
                      />
                    )}
                  </MDBox>
                  <Box
                    sx={{
                      pt: "0.8rem",
                      pr: "1rem",
                    }}
                  >
                    <KpiTrackerOptions openDailyGoalModal={() => setIsDailyGoalModalOpen(true)} />
                  </Box>
                </Stack>
                <Table aria-label="simple table">
                  {!isMobileDisplay && (
                    <TableHead sx={{ display: "table-header-group" }}>
                      <TableRow>
                        <TableCell align="left" />
                        <TableCell
                          sx={{
                            minWidth: {
                              xs: "150px",
                              sm: "200px",
                              md: "250px",
                              xxl: "300px",
                            },
                          }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center">
                            <MDTypography
                              sx={{
                                fontSize: "0.9rem",
                                fontWeight: "400",
                                color: "#888",
                              }}
                            >
                              KPI Name
                            </MDTypography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center" width="60%">
                          <MDTypography
                            sx={{
                              fontSize: "0.9rem",
                              fontWeight: "400",
                              color: "#888",
                            }}
                          >
                            Completed
                          </MDTypography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: "10%",
                            padding: "0.4rem 1rem",
                          }}
                        >
                          <MDTypography
                            sx={{
                              fontSize: "0.9rem",
                              fontWeight: "400",
                              color: "#888",
                              p: "0 0.5rem",
                              lineHeight: "1rem",
                            }}
                          >
                            Achieved Points
                          </MDTypography>
                        </TableCell>
                        {hasIconRow && (
                          <TableCell align="right" width="150px">
                            {lastSynced && (
                              <Tooltip title={lastSynced}>
                                <HistoryIcon fontSize="small" />
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                  )}
                  {kpiEntries && kpiEntries.length > 0 ? (
                    <TableBody>
                      {kpiEntries?.map((row) => (
                        <Draggable
                          key={`${row.id}-${row.sortByOrder}`}
                          draggableId={`kpi-${row.id}`}
                          index={row.sortByOrder}
                        >
                          {(provided, snapshot) => (
                            <TableRow
                              key={row.id}
                              hover
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style
                              )}
                              onMouseEnter={() => setHoveredRow(row.id)}
                              height={isMobileDisplay ? "90px" : "65px"}
                            >
                              <TableCell
                                width={30}
                                sx={{
                                  padding: 0,
                                  paddingLeft: "0.6rem",
                                  display: snapshot.isDragging ? "none" : "table-cell",
                                }}
                                {...provided.dragHandleProps}
                              >
                                <DragIndicatorIcon
                                  fontSize="small"
                                  sx={{
                                    alignSelf: "center",
                                    display:
                                      !snapshot.isDragging && hoveredRow === row.id
                                        ? "block"
                                        : "none",
                                  }}
                                />
                              </TableCell>
                              {isMobileDisplay ? (
                                <TableCell
                                  sx={{
                                    padding: "0.4rem 0 0 0",
                                    minWidth: "150px",
                                  }}
                                  {...provided.dragHandleProps}
                                >
                                  <Grid container direction="column">
                                    <Grid item display="flex" justifyContent="space-between">
                                      <Box display="flex" alignItems="center">
                                        <MDBadgeDot
                                          color="warning"
                                          size="lg"
                                          sx={{
                                            p: 0.5,
                                            i: {
                                              background: generateColorFromString(
                                                row.KpiActivity?.name
                                              ),
                                            },
                                          }}
                                          badgeContent=""
                                        />
                                        <MDTypography
                                          title={row?.KpiActivity?.name}
                                          sx={{
                                            fontSize: "1rem",
                                            fontWeight: "300",
                                            lineHeight: "1.1rem",
                                            pr: "0.5rem",
                                          }}
                                        >
                                          {row?.KpiActivity?.name}
                                        </MDTypography>
                                        {generateTrophies(row)}
                                      </Box>
                                      {/* <Box pl="0.25rem">{generateTrophies(row)}</Box> */}
                                    </Grid>
                                    <Grid
                                      item
                                      display="flex"
                                      justifyContent="space-between"
                                      sx={{ pb: "0.25rem" }}
                                    >
                                      {renderKpiInput(row)}
                                      <Box display="inline-flex" alignItems="baseline" pr="1rem">
                                        <Typography variant="h3" sx={{ fontWeight: "400" }}>
                                          {numberFormatter(
                                            parseInt(row?.DailyActions?.[0]?.points, 10)
                                          )}
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.7rem", px: "0.25rem" }}>
                                          {row?.DailyActions?.[0]?.points &&
                                          parseInt(row?.DailyActions?.[0]?.points, 10) === 1
                                            ? "pt"
                                            : "pts"}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </TableCell>
                              ) : (
                                <>
                                  <TableCell
                                    sx={{
                                      padding: "0.4rem 0.1rem",
                                    }}
                                    {...provided.dragHandleProps}
                                  >
                                    <Grid
                                      container
                                      sx={{ flexWrap: "nowrap", alignItems: "center" }}
                                    >
                                      <Grid item>
                                        <MDBadgeDot
                                          color="warning"
                                          size="lg"
                                          sx={{
                                            p: 0.5,
                                            i: {
                                              background: generateColorFromString(
                                                row.KpiActivity?.name
                                              ),
                                            },
                                          }}
                                        />
                                      </Grid>
                                      <Grid item>
                                        <MDTypography
                                          sx={{ fontSize: "1rem", fontWeight: "300", pr: "0.5rem" }}
                                        >
                                          {row?.KpiActivity?.name}
                                        </MDTypography>
                                      </Grid>
                                      <Grid item>{generateTrophies(row)}</Grid>
                                    </Grid>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      padding: "0.4rem 0.25rem",
                                    }}
                                    onDragStart={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                  >
                                    {renderKpiInput(row)}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      padding: "0.4rem 1rem",
                                      fontSize: "1.4rem",
                                    }}
                                    align="center"
                                  >
                                    {numberFormatter(parseInt(row?.DailyActions?.[0]?.points, 10))}
                                  </TableCell>
                                </>
                              )}
                              {hasIconRow && (
                                <TableCell
                                  sx={{
                                    textAlign: "right",
                                    pl: "0.25rem",
                                    py: "0.25rem",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: isMobileDisplay ? "90px" : "65px",
                                  }}
                                >
                                  {!!row.hasDelay && (
                                    <TooltipLight
                                      title="Sync delay of 5 minutes"
                                      placement="bottom"
                                      variant="outline"
                                    >
                                      <Forward5Icon fontSize="small" sx={{ color: "#9ca4b1" }} />
                                    </TooltipLight>
                                  )}
                                  {!!(row.isSynced && row.isKpiLocked) && (
                                    <TooltipLight
                                      title={row.isKpiLockedTooltip}
                                      placement="bottom"
                                      variant="outline"
                                    >
                                      <LockIcon fontSize="small" sx={{ color: "#9ca4b1" }} />
                                    </TooltipLight>
                                  )}
                                  {!!(row.isSynced && row.syncDirection === "pull") &&
                                    (Array.isArray(row.isSyncedTooltip) ? (
                                      <PopperWithHtml data={row.isSyncedTooltip}>
                                        <MDButton
                                          sx={{
                                            cursor: "pointer",
                                            minHeight: "unset",
                                            minWidth: "unset",
                                            width: "1rem",
                                            height: "2rem",
                                            mb: "-0.15rem",
                                            "&:hover": {
                                              background: `${theme.palette.gray40.main}10`,
                                            },
                                          }}
                                        >
                                          <ArrowBackIcon
                                            fontSize="small"
                                            sx={{ color: "#9ca4b1" }}
                                          />
                                        </MDButton>
                                      </PopperWithHtml>
                                    ) : (
                                      <TooltipLight
                                        title={row.isSyncedTooltip}
                                        placement="bottom"
                                        variant="outline"
                                      >
                                        <ArrowBackIcon fontSize="small" sx={{ color: "#9ca4b1" }} />
                                      </TooltipLight>
                                    ))}
                                  {!!(row.isSynced && row.syncDirection === "bidirectional") && (
                                    <TooltipLight
                                      title={row.isSyncedTooltip}
                                      placement="bottom"
                                      variant="outline"
                                    >
                                      <SyncAltIcon fontSize="small" sx={{ color: "#9ca4b1" }} />
                                    </TooltipLight>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                    </TableBody>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell />
                        <TableCell>
                          <Typography sx={{ fontSize: "0.9rem" }}>No KPIs assigned!</Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
                {droppableProvided.placeholder}
              </TableContainer>
            )}
          </Droppable>
        </DragDropContext>
      </Grid>
      <DailyPointsInformationModal
        handleClose={() => setIsDailyPointsInfoOpen((prev) => !prev)}
        open={isDailyPointsInfoOpen}
        actionInfo={dailyInfo}
        loading={dailyInfoLoading}
      />
      {isDailyGoalModalOpen && (
        <DailyGoalModal
          open={isDailyGoalModalOpen}
          handleClose={() => setIsDailyGoalModalOpen((prev) => !prev)}
          kpiEntries={kpiEntries}
          loading={kpiEntriesLoading}
          handleSave={handleSaveDailyGoals}
        />
      )}
      {/* Leaving in for now, will bring back more expanded functionality on this later */}
      {/* <Modal
        open={modalData.open}
        onClose={() => {
          modalData.cb({ ...modalFieldsState }, true);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: window.innerWidth < 400 ? "100%" : 650,
            height: window.innerWidth < 400 ? "100%" : 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              marginBottom: "24px",
            }}
          >
            Required Fields
          </Typography>
          <form
            onSubmit={(e) => {
              e?.preventDefault?.();
              modalData.cb({ ...modalFieldsState });
            }}
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {modalData.fields.map((field) => (
              <MDInput
                sx={{
                  marginBottom: "16px",
                }}
                key={field.apiName}
                label={field.label}
                value={modalFieldsState[field.apiName].value || ""}
                onChange={(e) =>
                  setModalFieldsState((prev) => ({
                    ...prev,
                    [field.apiName]: {
                      value: e.target.value,
                      touched: true,
                      error: !e.target.value,
                    },
                  }))
                }
                error={
                  modalFieldsState?.[field.apiName]?.touched &&
                  modalFieldsState?.[field.apiName]?.error
                }
              />
            ))}
            <MDButton
              type="submit"
              color="newColor"
              sx={{
                width: "200px",
                position: "absolute",
                bottom: "0",
                marginBottom: "24px",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              Save
            </MDButton>
          </form>
        </Box>
      </Modal> */}
    </Grid>
  );
}

export default UserKpi;

UserKpi.propTypes = {
  // dailyInfo: PropTypes.array.isRequired,
  kpiEntriesLoading: PropTypes.bool.isRequired,
  dailyInfoLoading: PropTypes.bool.isRequired,
  kpiEntries: PropTypes.array.isRequired,
};
